// API Endpoint Constants
// Centralized API configuration for the DIDI Web Application

/**
 * Validates the API URL format.
 *
 * @param url - The URL to validate
 * @returns The validated URL (unchanged if valid)
 * @throws Error if the URL is non-empty but invalid
 */
const validateApiUrl = (url: string): string => {
  // Empty string is valid (same-origin relative paths)
  if (url === '') return url;

  // Non-empty must be a valid URL
  try {
    new URL(url);
    return url;
  } catch {
    throw new Error(
      `Invalid VITE_API_URL: "${url}". ` +
      'Must be empty string "" for same-origin or valid URL for cross-origin.'
    );
  }
};

/**
 * API Base URL Configuration
 *
 * Same-origin deployment: VITE_API_URL="" (empty string, relative paths)
 * Cross-origin deployment: VITE_API_URL="http://host:port" (absolute URL)
 *
 * Uses nullish coalescing to default undefined to empty string (same-origin).
 * Throws at startup if the URL format is invalid.
 */
export const API_BASE_URL = validateApiUrl(import.meta.env.VITE_API_URL ?? '');

/**
 * API version prefix
 * NOTE: Agent service uses /api (not /api/v1 like old orchestrator)
 */
export const API_VERSION = '/api';

/**
 * Full base path for API requests
 */
export const API_BASE_PATH = `${API_BASE_URL}${API_VERSION}`;

/**
 * Default request timeout in milliseconds (30 seconds)
 */
export const DEFAULT_TIMEOUT = 30000;

/**
 * Default polling interval for run status (2 seconds)
 */
export const DEFAULT_POLL_INTERVAL = 2000;

/**
 * Maximum wait time for run completion (5 minutes)
 */
export const MAX_WAIT_TIME = 300000;

/**
 * Auth endpoints (backend user registration, profile, credits)
 * All require Authorization: Bearer <keycloak_token>
 */
export const AUTH_ENDPOINTS = {
  /** POST - Register user profile in backend (201 = created, 409 = exists) */
  REGISTER: `${API_BASE_PATH}/auth/register`,
  /** GET /:code = invite info · POST /:code = create account (PUBLIC, no token) */
  JOIN: `${API_BASE_PATH}/auth/join`,
  /** GET - Get current user profile + credits */
  ME: `${API_BASE_PATH}/auth/me`,
  /** GET - Check available credits */
  CREDITS: `${API_BASE_PATH}/auth/credits`,
  /** POST - Deduct credit after analysis */
  USE_CREDIT: `${API_BASE_PATH}/auth/use-credit`,
} as const;

/**
 * History endpoints (V3 pipeline, PostgreSQL-backed, persistent)
 */
export const HISTORY_ENDPOINTS = {
  /** GET - List history with pagination: ?user_id={keycloak_id}&page=1&limit=20 */
  LIST: `${API_BASE_URL}/agent-v3/api/v3/pipeline/history`,
  /** GET - Full analysis detail by session_id */
  DETAIL: (sessionId: string) => `${API_BASE_URL}/agent-v3/api/v3/pipeline/history/${sessionId}`,
  /** DELETE - Remove from history: ?user_id={keycloak_id} */
  DELETE: (sessionId: string) => `${API_BASE_URL}/agent-v3/api/v3/pipeline/history/${sessionId}`,
} as const;

/**
 * Agent Analysis endpoints (direct agent service)
 * These connect to the staging-agent service for disinformation analysis
 */
export const AGENT_ENDPOINTS = {
  /** POST - Start full analysis with scoring */
  ANALYZE_FULL: `${API_BASE_PATH}/analyze/full`,
  /** GET - Poll progress during analysis */
  PROGRESS: (sessionId: string) => `${API_BASE_PATH}/progress/${sessionId}`,
  /** GET - Get full analysis result after completion */
  ANALYSIS: (sessionId: string) => `${API_BASE_PATH}/analysis/${sessionId}`,
  /** GET - Health check */
  HEALTH: `${API_BASE_PATH}/../health`,
  /** GET - Service status */
  STATUS: `${API_BASE_PATH}/status`,
} as const;

/**
 * Async analysis endpoints (RabbitMQ-based)
 * Submit returns 202 with session_id, then poll for status and fetch result.
 */
export const ASYNC_ENDPOINTS = {
  // Async
  PIPELINE: `${API_BASE_URL}/agent-v3/api/v3/pipeline/analyze-async`,
  TECHNIQUES: `${API_BASE_URL}/agent-v3/api/v3/techniques/analyze-async`,
  CLAIMS: `${API_BASE_URL}/agent-v3/api/v3/claims/analyze-async`,
  AI_TAMPERED: `${API_BASE_URL}/agent-v3/api/v3/ai-tampered/analyze-async`,
  DOMAIN: `${API_BASE_URL}/agent-v3/api/v3/domain/analyze-async`,
  QUEUE_STATUS: (sessionId: string) => `${API_BASE_URL}/agent-v3/api/v3/pipeline/${sessionId}/queue-status`,
  RESULT: (sessionId: string) => `${API_BASE_URL}/agent-v3/api/v3/pipeline/${sessionId}/result`,
  // Sync - standalone component (text)
  TECHNIQUES_SYNC: `${API_BASE_URL}/agent-v3/api/v3/techniques/analyze`,
  CLAIMS_SYNC: `${API_BASE_URL}/agent-v3/api/v3/claims/analyze`,
  AI_TAMPERED_SYNC: `${API_BASE_URL}/agent-v3/api/v3/ai-tampered/analyze`,
  // Sync - standalone component (media)
  TECHNIQUES_MEDIA: `${API_BASE_URL}/agent-v3/api/v3/techniques/analyze-media`,
  CLAIMS_MEDIA: `${API_BASE_URL}/agent-v3/api/v3/claims/analyze-media`,
  AI_TAMPERED_MEDIA: `${API_BASE_URL}/agent-v3/api/v3/ai-tampered/analyze-media`,
  // Sync - pipeline
  PIPELINE_SYNC: `${API_BASE_URL}/agent-v3/api/v3/pipeline/analyze`,
  PIPELINE_MEDIA: `${API_BASE_URL}/agent-v3/api/v3/pipeline/analyze-media`,
  // Pipeline status
  PIPELINE_STATUS: (sessionId: string) => `${API_BASE_URL}/agent-v3/api/v3/pipeline/${sessionId}/status`,
} as const;
